import { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, ChevronDown, ChevronRight, Package } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import TransferStockSheet from '../components/TransferStockSheet.jsx';
import { listWarehouses, listWarehouseStock } from '../api/warehouses';

// Product categories — values mirror the backend Product.category enum
// (pv_panel | inverter | battery | accessory). No "all" option by
// design; one of these is always active. Default = pv_panel because
// panels are the most-checked stock for field reps in this product
// mix.
const CATEGORIES = [
  { value: 'pv_panel',  label: 'Panels'      },
  { value: 'inverter',  label: 'Inverters'   },
  { value: 'battery',   label: 'Batteries'   },
  { value: 'accessory', label: 'Accessories' },
];

/**
 * Read-only stock view. Pick a warehouse, see batches with available qty.
 * Filter by product name with the search box.
 *
 * Online-only by design (per docs/PWA_BRIEF.md §3.3): stock allocations
 * change frequently and cached numbers would mislead the field rep about
 * what's actually available.
 */
export default function Stock() {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [stock, setStock] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingWh, setLoadingWh] = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [error, setError] = useState(null);

  // Transfer flow — `transferRow` holds the WarehouseStockItem the rep
  // tapped (null = sheet closed). On success we show a brief flash
  // confirming what moved where.
  const [transferRow, setTransferRow] = useState(null);
  const [flash, setFlash] = useState(null);

  // Set of product IDs whose batch list is currently expanded.
  // Cleared on warehouse change so an expanded card on warehouse A
  // doesn't confuse the rep when they switch to warehouse B.
  const [expandedProductIds, setExpandedProductIds] = useState(() => new Set());

  // Active category — one of the four enum values. Defaults to panels;
  // the rep can flip categories with the chips below the search bar.
  // No "all" option, no deselect: a category is always active.
  const [categoryFilter, setCategoryFilter] = useState('pv_panel');

  function toggleProduct(productKey) {
    setExpandedProductIds((curr) => {
      const next = new Set(curr);
      if (next.has(productKey)) next.delete(productKey);
      else next.add(productKey);
      return next;
    });
  }

  // Load warehouses on mount; auto-select the first one.
  useEffect(() => {
    let cancelled = false;
    listWarehouses()
      .then((rows) => {
        if (cancelled) return;
        setWarehouses(rows);
        if (rows.length > 0) setWarehouseId(rows[0].id);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoadingWh(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load stock whenever warehouse changes. Also reset the
  // expanded-products set so the rep doesn't carry stale expand state
  // across warehouses.
  useEffect(() => {
    if (!warehouseId) return;
    let cancelled = false;
    setLoadingStock(true);
    setError(null);
    setExpandedProductIds(new Set());
    listWarehouseStock(warehouseId)
      .then((rows) => !cancelled && setStock(rows))
      .catch((err) => !cancelled && setError(err.response?.data?.detail || err.message))
      .finally(() => !cancelled && setLoadingStock(false));
    return () => {
      cancelled = true;
    };
  }, [warehouseId]);

  // Re-fetch the current warehouse's stock — used after a successful
  // transfer so the source row reflects the new available qty.
  async function refreshStock() {
    if (!warehouseId) return;
    try {
      const rows = await listWarehouseStock(warehouseId);
      setStock(rows);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }

  function onTransferSuccess({ productName, quantity, toWarehouseName }) {
    setTransferRow(null);
    setFlash(
      `Moved ${quantity} × ${productName ?? 'units'} → ${toWarehouseName ?? 'destination'}`,
    );
    refreshStock();
    // Clear the flash after a few seconds so it doesn't linger.
    setTimeout(() => setFlash(null), 4_000);
  }

  const sourceWarehouse = warehouses.find((w) => w.id === warehouseId) ?? null;

  // Category + search filter on the raw rows. Applied per-batch
  // because that's what listWarehouseStock returns; the grouping
  // memo below collapses the surviving rows into product groups.
  // Rows without a product_category (orphan / legacy data) are
  // excluded from category-filtered views so they don't show up
  // accidentally — they'd only appear if we re-introduce an "all"
  // chip later.
  const filtered = stock.filter((row) => {
    if (row.product_category !== categoryFilter) return false;
    if (search) {
      return (row.product_name || '').toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  // Group filtered rows by product so the list renders one row per
  // product (with totals across batches), not per batch. The
  // accordion expands each row to reveal its constituent batches.
  // `productKey` defaults to product_id but falls back to the name +
  // batch_id for the rare case where a backend row is missing the
  // product_id (treated as "unknown" — kept separate from real
  // products).
  const productGroups = useMemo(() => {
    const map = new Map();
    for (const row of filtered) {
      const key = row.product_id != null
        ? `p-${row.product_id}`
        : `b-${row.batch_id}`; // ungrouped fallback
      if (!map.has(key)) {
        map.set(key, {
          key,
          product_id:    row.product_id ?? null,
          product_name:  row.product_name ?? `Batch ${row.batch_id}`,
          total_available: 0,
          total_on_hold:   0,
          batches:         [],
        });
      }
      const group = map.get(key);
      group.total_available += Number(row.quantity_available) || 0;
      group.total_on_hold   += Number(row.quantity_on_hold)   || 0;
      group.batches.push(row);
    }
    // Stable order: by product name.
    return Array.from(map.values()).sort((a, b) =>
      (a.product_name || '').localeCompare(b.product_name || ''),
    );
  }, [filtered]);

  return (
    <main className="min-h-full px-4 pb-6">
      <PageHeader title="Stock" subtitle="Check what's on hand" icon={Package} />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {loadingWh ? (
        <p className="text-small text-gray-500 text-center py-8">Loading warehouses…</p>
      ) : warehouses.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-10 w-10 mx-auto mb-3 text-gray-400" />
          <p className="text-body text-gray-700">No warehouses configured</p>
          <p className="text-small text-gray-500 mt-1">Set one up in the desktop ERP first.</p>
        </div>
      ) : (
        <>
          {/* Warehouse selector — chips when ≤ 4, dropdown otherwise. */}
          {warehouses.length <= 4 ? (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              {warehouses.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setWarehouseId(w.id)}
                  className={`min-h-touch px-4 rounded-pill text-small font-medium whitespace-nowrap transition-colors ${
                    w.id === warehouseId
                      ? 'bg-amber-500 text-white border border-amber-500'
                      : 'bg-surface border border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {w.name}
                </button>
              ))}
            </div>
          ) : (
            <select
              value={warehouseId ?? ''}
              onChange={(e) => setWarehouseId(Number(e.target.value))}
              className="w-full h-10 px-3 border border-gray-300 rounded-md text-body bg-surface hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          )}

          {/* Search */}
          <input
            type="search"
            placeholder="Search product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mt-3 h-10 px-3 border border-gray-300 rounded-md text-body hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />

          {/* Category filter — one is always active, no "all" option,
              no tap-to-deselect. Switching is always tap-another-chip.
              Same amber active-state styling as the warehouse chips
              above for consistency. */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mt-3 -mx-4 px-4">
            {CATEGORIES.map((c) => {
              const active = categoryFilter === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategoryFilter(c.value)}
                  className={`min-h-touch px-4 rounded-pill text-small font-medium whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-amber-500 text-white border border-amber-500'
                      : 'bg-surface border border-gray-300 text-gray-700'
                  }`}
                  aria-pressed={active}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Stock list */}
          <section className="mt-3 space-y-2">
            {loadingStock && (
              <p className="text-small text-gray-500 text-center py-8">Loading…</p>
            )}
            {error && (
              <p className="text-small text-danger text-center py-4">{error}</p>
            )}
            {!loadingStock && !error && productGroups.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <p className="text-body text-gray-700">
                  {search
                    ? 'No matches'
                    : `No ${
                        CATEGORIES.find((c) => c.value === categoryFilter)?.label.toLowerCase()
                          ?? 'stock'
                      } at this warehouse`}
                </p>
              </div>
            )}
            {!loadingStock &&
              productGroups.map((group) => {
                const expanded = expandedProductIds.has(group.key);
                const batchCount = group.batches.length;
                return (
                  <div key={group.key} className="mb-card overflow-hidden p-0">
                    {/* Product row — tap toggles the accordion. The
                        whole row is the touch target so reps can hit
                        anywhere on it; chevron rotates to signal
                        expanded state. */}
                    <button
                      type="button"
                      onClick={() => toggleProduct(group.key)}
                      aria-expanded={expanded}
                      className="w-full text-left flex items-center gap-3 p-4 active:bg-gray-50 transition-colors"
                    >
                      {expanded ? (
                        <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-body font-medium text-gray-900 truncate">
                          {group.product_name}
                        </p>
                        <p className="text-caption text-gray-500">
                          {batchCount} batch{batchCount === 1 ? '' : 'es'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-title font-semibold text-gray-900">
                          {group.total_available}
                        </p>
                        <p className="text-caption text-gray-500">available</p>
                        {group.total_on_hold > 0 && (
                          <p className="text-caption text-amber-700">
                            {group.total_on_hold} on hold
                          </p>
                        )}
                      </div>
                    </button>

                    {/* Expanded batch list — visible only when the
                        product is open. Each batch retains the
                        existing tap-to-transfer affordance. The
                        amber "Tap to move" hint sits on the batch
                        row, not the product row, so reps don't think
                        the product itself is movable. */}
                    {expanded && (
                      <ul className="border-t border-gray-200 bg-gray-50 divide-y divide-gray-200">
                        {group.batches.map((row) => {
                          const transferable =
                            row.quantity_available > 0 && warehouses.length > 1;
                          return (
                            <li key={row.batch_id}>
                              <button
                                type="button"
                                onClick={() => transferable && setTransferRow(row)}
                                disabled={!transferable}
                                className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 ${
                                  transferable
                                    ? 'active:bg-gray-100'
                                    : 'opacity-80 cursor-default'
                                } transition-colors`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-small font-medium text-gray-800">
                                    {row.order_number ?? `Batch ${row.batch_id}`}
                                  </p>
                                  {transferable && (
                                    <p className="text-caption text-amber-700 mt-0.5 inline-flex items-center gap-1">
                                      <ArrowRightLeft className="h-3 w-3" />
                                      Tap to move
                                    </p>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-body font-semibold text-gray-900">
                                    {row.quantity_available}
                                  </p>
                                  {row.quantity_on_hold > 0 && (
                                    <p className="text-caption text-amber-700">
                                      {row.quantity_on_hold} on hold
                                    </p>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
          </section>
        </>
      )}

      {/* Transfer sheet — mounted only when a row is selected so its
          internal form state (destination, quantity, reason) resets
          cleanly on each open. */}
      {transferRow && sourceWarehouse && (
        <TransferStockSheet
          row={transferRow}
          sourceWarehouse={sourceWarehouse}
          warehouses={warehouses}
          onClose={() => setTransferRow(null)}
          onSuccess={onTransferSuccess}
        />
      )}
    </main>
  );
}
